import type {
  JsonRecord,
  TelemetryLogRow,
  TelemetryRuntimeConfig,
  TelemetryTraceRow,
  UnifiedEvent,
} from "@/lib/types";

const SESSION_KEYS = ["w7s.session.id", "sessionId", "session_id", "app.session.id"];
const SESSION_EVENT_KEYS = [
  "w7s.session.event.id",
  "sessionEventId",
  "session_event_id",
  "app.session.event.id",
];
const USER_KEYS = ["w7s.user.id", "enduser.id", "user.id", "userId"];

const parseObjectJson = (value: string | null | undefined): JsonRecord => {
  if (!value || typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as JsonRecord;
    }
  } catch {
    // no-op
  }
  return {};
};

const asString = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
};

const getValueByKey = (record: JsonRecord, key: string): unknown => {
  if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
  if (!key.includes(".")) return undefined;
  const parts = key.split(".");
  let pointer: unknown = record;
  for (const part of parts) {
    if (!pointer || typeof pointer !== "object" || Array.isArray(pointer)) return undefined;
    pointer = (pointer as JsonRecord)[part];
  }
  return pointer;
};

const extractByKeys = (keys: string[], ...records: JsonRecord[]): string | null => {
  for (const key of keys) {
    for (const record of records) {
      const value = getValueByKey(record, key);
      const output = asString(value);
      if (output) return output;
    }
  }
  return null;
};

const toMsFromNano = (value: string | null | undefined): number | null => {
  const normalized = asString(value);
  if (!normalized) return null;
  try {
    const int = BigInt(normalized);
    return Number(int / 1_000_000n);
  } catch {
    return null;
  }
};

const stableEventKey = (kind: "log" | "span", row: { id: string; ingestedAtMs: number; traceId: string | null; spanId: string | null }) =>
  `${kind}:${row.id}:${row.ingestedAtMs}:${row.traceId ?? ""}:${row.spanId ?? ""}`;

const decodeOtlpAnyValue = (value: unknown): unknown => {
  if (!value || typeof value !== "object") return value;
  const record = value as JsonRecord;
  if ("stringValue" in record) return record.stringValue;
  if ("boolValue" in record) return record.boolValue;
  if ("intValue" in record) return record.intValue;
  if ("doubleValue" in record) return record.doubleValue;
  if ("bytesValue" in record) return record.bytesValue;
  const arrayValue = record.arrayValue;
  if (arrayValue && typeof arrayValue === "object") {
    const values = (arrayValue as JsonRecord).values;
    if (Array.isArray(values)) {
      return values.map((entry) => decodeOtlpAnyValue(entry));
    }
  }
  const kvListValue = record.kvlistValue;
  if (kvListValue && typeof kvListValue === "object") {
    const values = (kvListValue as JsonRecord).values;
    if (Array.isArray(values)) {
      const out: JsonRecord = {};
      for (const item of values) {
        if (!item || typeof item !== "object") continue;
        const key = asString((item as JsonRecord).key);
        if (!key) continue;
        out[key] = decodeOtlpAnyValue((item as JsonRecord).value);
      }
      return out;
    }
  }
  return value;
};

const decodeOtlpAttributes = (attributes: unknown): JsonRecord => {
  if (!Array.isArray(attributes)) return {};
  const out: JsonRecord = {};
  for (const item of attributes) {
    if (!item || typeof item !== "object") continue;
    const key = asString((item as JsonRecord).key);
    if (!key) continue;
    out[key] = decodeOtlpAnyValue((item as JsonRecord).value);
  }
  return out;
};

const resolveMessageForLog = (row: TelemetryLogRow): string => {
  const body = asString(row.bodyText);
  return body ?? "(empty log body)";
};

const resolveMessageForSpan = (row: TelemetryTraceRow): string => {
  const name = asString(row.spanName) ?? "span";
  return `${name}${row.statusCode === 2 ? " (error)" : ""}`;
};

export const logToUnifiedEvent = (row: TelemetryLogRow): UnifiedEvent => {
  const attributes = parseObjectJson(row.attributesJson);
  const resourceAttributes = parseObjectJson(row.resourceAttributesJson);
  const tsMs =
    toMsFromNano(row.timeUnixNano) ?? toMsFromNano(row.observedTimeUnixNano) ?? row.ingestedAtMs;
  const sessionId = extractByKeys(SESSION_KEYS, attributes, resourceAttributes);
  const sessionEventId = extractByKeys(SESSION_EVENT_KEYS, attributes, resourceAttributes);
  const userId = extractByKeys(USER_KEYS, attributes, resourceAttributes);
  return {
    id: row.id,
    key: stableEventKey("log", row),
    kind: "log",
    tsMs,
    traceId: asString(row.traceId),
    spanId: asString(row.spanId),
    parentSpanId: null,
    sessionId,
    sessionEventId,
    userId,
    serviceName: asString(row.serviceName),
    message: resolveMessageForLog(row),
    severityText: asString(row.severityText),
    severityNumber: typeof row.severityNumber === "number" ? row.severityNumber : null,
    statusCode: null,
    statusMessage: null,
    durationMs: null,
    attributes,
    resourceAttributes,
    raw: row,
  };
};

export const spanToUnifiedEvent = (row: TelemetryTraceRow): UnifiedEvent => {
  const attributes = parseObjectJson(row.attributesJson);
  const resourceAttributes = parseObjectJson(row.resourceAttributesJson);
  const startTs = toMsFromNano(row.startTimeUnixNano);
  const endTs = toMsFromNano(row.endTimeUnixNano);
  const sessionId = extractByKeys(SESSION_KEYS, attributes, resourceAttributes);
  const sessionEventId = extractByKeys(SESSION_EVENT_KEYS, attributes, resourceAttributes);
  const userId = extractByKeys(USER_KEYS, attributes, resourceAttributes);
  return {
    id: row.id,
    key: stableEventKey("span", {
      id: row.id,
      ingestedAtMs: row.ingestedAtMs,
      traceId: row.traceId,
      spanId: row.spanId,
    }),
    kind: "span",
    tsMs: startTs ?? row.ingestedAtMs,
    traceId: asString(row.traceId),
    spanId: asString(row.spanId),
    parentSpanId: asString(row.parentSpanId),
    sessionId,
    sessionEventId,
    userId,
    serviceName: asString(row.serviceName),
    message: resolveMessageForSpan(row),
    severityText: null,
    severityNumber: null,
    statusCode: typeof row.statusCode === "number" ? row.statusCode : null,
    statusMessage: asString(row.statusMessage),
    durationMs:
      typeof startTs === "number" && typeof endTs === "number" ? Math.max(0, endTs - startTs) : null,
    attributes,
    resourceAttributes,
    raw: row,
  };
};

const parseResourceLogs = (payload: JsonRecord): UnifiedEvent[] => {
  const resourceLogs = payload.resourceLogs;
  if (!Array.isArray(resourceLogs)) return [];
  const events: UnifiedEvent[] = [];
  let sequence = 0;

  for (const resourceLog of resourceLogs) {
    if (!resourceLog || typeof resourceLog !== "object") continue;
    const resource = (resourceLog as JsonRecord).resource;
    const resourceAttributes = decodeOtlpAttributes(
      resource && typeof resource === "object" ? (resource as JsonRecord).attributes : undefined,
    );
    const serviceName = asString(resourceAttributes["service.name"]);
    const scopeLogs = (resourceLog as JsonRecord).scopeLogs;
    if (!Array.isArray(scopeLogs)) continue;

    for (const scopeLog of scopeLogs) {
      if (!scopeLog || typeof scopeLog !== "object") continue;
      const records = (scopeLog as JsonRecord).logRecords;
      if (!Array.isArray(records)) continue;
      for (const record of records) {
        if (!record || typeof record !== "object") continue;
        const logRecord = record as JsonRecord;
        const attributes = decodeOtlpAttributes(logRecord.attributes);
        const sessionId = extractByKeys(SESSION_KEYS, attributes, resourceAttributes);
        const sessionEventId = extractByKeys(SESSION_EVENT_KEYS, attributes, resourceAttributes);
        const userId = extractByKeys(USER_KEYS, attributes, resourceAttributes);
        const timeMs =
          toMsFromNano(asString(logRecord.timeUnixNano)) ??
          toMsFromNano(asString(logRecord.observedTimeUnixNano)) ??
          Date.now();
        const message = asString(decodeOtlpAnyValue(logRecord.body)) ?? "(empty log body)";
        const traceId = asString(logRecord.traceId);
        const spanId = asString(logRecord.spanId);
        const id = `${timeMs}-${sequence}-${traceId ?? ""}-${spanId ?? ""}`;
        sequence += 1;
        events.push({
          id,
          key: `live:log:${id}`,
          kind: "log",
          tsMs: timeMs,
          traceId,
          spanId,
          parentSpanId: null,
          sessionId,
          sessionEventId,
          userId,
          serviceName,
          message,
          severityText: asString(logRecord.severityText),
          severityNumber:
            typeof logRecord.severityNumber === "number" ? logRecord.severityNumber : null,
          statusCode: null,
          statusMessage: null,
          durationMs: null,
          attributes,
          resourceAttributes,
          raw: record,
        });
      }
    }
  }

  return events;
};

const parseResourceSpans = (payload: JsonRecord): UnifiedEvent[] => {
  const resourceSpans = payload.resourceSpans;
  if (!Array.isArray(resourceSpans)) return [];
  const events: UnifiedEvent[] = [];
  let sequence = 0;

  for (const resourceSpan of resourceSpans) {
    if (!resourceSpan || typeof resourceSpan !== "object") continue;
    const resource = (resourceSpan as JsonRecord).resource;
    const resourceAttributes = decodeOtlpAttributes(
      resource && typeof resource === "object" ? (resource as JsonRecord).attributes : undefined,
    );
    const serviceName = asString(resourceAttributes["service.name"]);
    const scopeSpans = (resourceSpan as JsonRecord).scopeSpans;
    if (!Array.isArray(scopeSpans)) continue;

    for (const scopeSpan of scopeSpans) {
      if (!scopeSpan || typeof scopeSpan !== "object") continue;
      const spans = (scopeSpan as JsonRecord).spans;
      if (!Array.isArray(spans)) continue;
      for (const spanRaw of spans) {
        if (!spanRaw || typeof spanRaw !== "object") continue;
        const span = spanRaw as JsonRecord;
        const attributes = decodeOtlpAttributes(span.attributes);
        const sessionId = extractByKeys(SESSION_KEYS, attributes, resourceAttributes);
        const sessionEventId = extractByKeys(SESSION_EVENT_KEYS, attributes, resourceAttributes);
        const userId = extractByKeys(USER_KEYS, attributes, resourceAttributes);
        const startMs = toMsFromNano(asString(span.startTimeUnixNano));
        const endMs = toMsFromNano(asString(span.endTimeUnixNano));
        const traceId = asString(span.traceId);
        const spanId = asString(span.spanId);
        const id = `${startMs ?? Date.now()}-${sequence}-${traceId ?? ""}-${spanId ?? ""}`;
        sequence += 1;
        const status = span.status && typeof span.status === "object" ? (span.status as JsonRecord) : {};
        events.push({
          id,
          key: `live:span:${id}`,
          kind: "span",
          tsMs: startMs ?? Date.now(),
          traceId,
          spanId,
          parentSpanId: asString(span.parentSpanId),
          sessionId,
          sessionEventId,
          userId,
          serviceName,
          message: asString(span.name) ?? "span",
          severityText: null,
          severityNumber: null,
          statusCode: typeof status.code === "number" ? status.code : null,
          statusMessage: asString(status.message),
          durationMs:
            typeof startMs === "number" && typeof endMs === "number" ? Math.max(0, endMs - startMs) : null,
          attributes,
          resourceAttributes,
          raw: span,
        });
      }
    }
  }

  return events;
};

export const parseRealtimeEnvelope = (topic: string, payload: unknown): UnifiedEvent[] => {
  if (!payload || typeof payload !== "object") return [];
  const record = payload as JsonRecord;
  if (topic === "logs") return parseResourceLogs(record);
  if (topic === "traces") return parseResourceSpans(record);
  if (Array.isArray(record.resourceLogs)) return parseResourceLogs(record);
  if (Array.isArray(record.resourceSpans)) return parseResourceSpans(record);
  return [];
};

export const isErrorEvent = (event: UnifiedEvent): boolean => {
  if (event.kind === "span") return event.statusCode === 2;
  const text = (event.severityText ?? "").toLowerCase();
  if (text === "error" || text === "fatal") return true;
  if (typeof event.severityNumber === "number" && event.severityNumber >= 17) return true;
  return false;
};

export const sortEventsDesc = (items: UnifiedEvent[]): UnifiedEvent[] =>
  [...items].sort((a, b) => b.tsMs - a.tsMs || a.key.localeCompare(b.key));

export const sortEventsAsc = (items: UnifiedEvent[]): UnifiedEvent[] =>
  [...items].sort((a, b) => a.tsMs - b.tsMs || a.key.localeCompare(b.key));

export const readRuntimeConfig = (): TelemetryRuntimeConfig => {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const pathOrg = window.location.pathname
    .split("/")
    .filter(Boolean)[0]
    ?.trim()
    .toLowerCase();
  const paramOrg = params.get("org")?.trim().toLowerCase();
  const org = paramOrg || pathOrg || "";

  const telemetryOrigin =
    (params.get("telemetryOrigin") || "https://telemetry.w7s.io").trim().replace(/\/+$/, "") ||
    "https://telemetry.w7s.io";

  const queryToken =
    params.get("token")?.trim() || params.get("apiToken")?.trim() || params.get("bearer")?.trim() || "";

  const storedToken =
    window.localStorage.getItem("w7s.telemetry.bearer")?.trim() ||
    window.sessionStorage.getItem("w7s.telemetry.bearer")?.trim() ||
    "";

  const authToken = queryToken || storedToken || null;

  return {
    org,
    telemetryOrigin,
    authToken,
  };
};
